import { useCallback, useEffect, useState } from 'react';
import { ApiError, startTrial } from '@/lib/api';
import { Icon } from '@/components/Icon';
import { cloudSet, getWebApp, haptic, notify } from '@/lib/telegram';

export const ONBOARDING_COMPLETE_KEY = 'onboardingComplete';

interface Slide {
  eyebrow: string;
  title: React.ReactNode;
  body: string;
}

const SLIDES: Slide[] = [
  {
    eyebrow: 'shushvpn · знакомство',
    title: (
      <>
        интернет —<br />
        <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>как был.</span>
      </>
    ),
    body: 'инстаграм, ютуб, chatgpt, claude, твиттер. мы выдаём ключ — дальше всё работает',
  },
  {
    eyebrow: 'без оговорок',
    title: (
      <>
        не храним<br />
        <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>логов.</span>
      </>
    ),
    body: 'зарабатываем только на подписках. не показываем рекламу. не продаём данные. это всё',
  },
  {
    eyebrow: '3 дня бесплатно',
    title: (
      <>
        попробуй,
        <br />
        прежде чем
        <br />
        <span style={{ fontStyle: 'italic', color: 'var(--moss-deep)' }}>доверять.</span>
      </>
    ),
    body: 'триал — без карты, без звёзд, без ловушек. понравится — продлишь',
  },
];

interface OnboardingScreenProps {
  // Called after the user finishes onboarding (skip, trial start, or "сразу к
  // подписке"). The host App decides where to land: home for skip/trial,
  // paywall for the "skip to paywall" CTA. The boolean argument is true when
  // the user just started a trial — the host refetches /me in that case.
  onFinish: (opts: { startedTrial: boolean; gotoPaywall: boolean }) => void;
  onError: (msg: string) => void;
}

export function OnboardingScreen({ onFinish, onError }: OnboardingScreenProps) {
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const isLast = i === SLIDES.length - 1;

  // Persist completion *before* the host navigates so a tab-close mid-render
  // still leaves the flag set — losing the flag would re-show onboarding on
  // the next launch, which is exactly the bug to avoid.
  const markComplete = useCallback(async () => {
    await cloudSet(ONBOARDING_COMPLETE_KEY, '1');
  }, []);

  const skip = useCallback(async () => {
    haptic('light');
    await markComplete();
    onFinish({ startedTrial: false, gotoPaywall: false });
  }, [markComplete, onFinish]);

  const startTrialAndFinish = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    haptic('medium');
    try {
      await startTrial();
      notify('success');
      await markComplete();
      onFinish({ startedTrial: true, gotoPaywall: false });
    } catch (err) {
      // 409 means the user already used their trial (likely via the bot
      // before opening the mini app). Treat as a soft success — the home
      // screen will render their current state correctly.
      if (err instanceof ApiError && err.status === 409) {
        await markComplete();
        onFinish({ startedTrial: true, gotoPaywall: false });
        return;
      }
      notify('error');
      onError('не получилось активировать триал. попробуй ещё раз.');
    } finally {
      setBusy(false);
    }
  }, [busy, markComplete, onFinish, onError]);

  const skipToPaywall = useCallback(async () => {
    haptic('light');
    await markComplete();
    onFinish({ startedTrial: false, gotoPaywall: true });
  }, [markComplete, onFinish]);

  // Telegram BackButton: on slides 2+, pop back a step instead of closing.
  // First slide leaves the BackButton hidden so the user uses the close icon.
  useEffect(() => {
    const back = getWebApp()?.BackButton;
    if (!back) return;
    if (i > 0) {
      const handler = () => setI((cur) => Math.max(0, cur - 1));
      back.show();
      back.onClick(handler);
      return () => {
        back.offClick(handler);
        back.hide();
      };
    }
    back.hide();
    return undefined;
  }, [i]);

  const s = SLIDES[i];

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 18px 0' }}>
        <button
          onClick={() => void skip()}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--ink-3)',
            fontSize: 13,
            padding: 8,
          }}
        >
          пропустить →
        </button>
      </div>

      <div
        style={{
          flex: 1,
          padding: '12px 24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div className="eyebrow" style={{ marginBottom: 28 }}>
          {s.eyebrow}
        </div>
        <h1
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontWeight: 400,
            fontSize: 56,
            lineHeight: 0.95,
            margin: 0,
            color: 'var(--ink)',
            letterSpacing: '-0.025em',
          }}
        >
          {s.title}
        </h1>
        <p
          style={{
            fontSize: 14.5,
            color: 'var(--ink-2)',
            lineHeight: 1.55,
            marginTop: 26,
            maxWidth: 320,
          }}
        >
          {s.body}
        </p>

        <div style={{ display: 'flex', gap: 6, marginTop: 38 }}>
          {SLIDES.map((_, k) => (
            <div
              key={k}
              style={{
                width: k === i ? 22 : 6,
                height: 6,
                borderRadius: 3,
                background: k === i ? 'var(--ink)' : 'var(--paper-3)',
                transition: 'width 220ms',
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ padding: '0 18px 28px' }}>
        {!isLast ? (
          <button
            onClick={() => {
              haptic('light');
              setI((cur) => Math.min(SLIDES.length - 1, cur + 1));
            }}
            className="btn btn-primary"
            style={{
              width: '100%',
              height: 56,
              fontSize: 14.5,
              justifyContent: 'space-between',
              padding: '0 22px',
            }}
          >
            дальше
            <Icon name="arrow" size={16} stroke="var(--paper)" sw={1.8} />
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={() => void startTrialAndFinish()}
              disabled={busy}
              className="btn btn-moss"
              style={{
                width: '100%',
                height: 56,
                fontSize: 14.5,
                opacity: busy ? 0.6 : 1,
              }}
            >
              <Icon name="bolt" size={16} stroke="#fff" sw={1.8} />
              {busy ? 'активируем…' : 'начать триал на 3 дня'}
            </button>
            <button
              onClick={() => void skipToPaywall()}
              disabled={busy}
              className="btn btn-ghost"
              style={{ width: '100%', height: 50, fontSize: 13.5 }}
            >
              сразу к подписке
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
